import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { TokenService } from '../services/tokenService';
import { formatNumber } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import QRCode from 'qrcode';
import { faFile, faIdCard, faIdCardAlt, faFileInvoiceDollar, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import DocumentItem from '../components/DocumentItem/PendingDocumentItem';
import '../styles/documentUpload.css';
import '../styles/saleStatusBadges.css';
import {
  getApprovalStatusClass,
  getApprovalStatusLabel,
  getSettlementStatusClass,
  getSettlementStatusLabel,
} from '../utils/saleStatusAdapter';
import { getSaleLoanIdentifierDisplay } from '../utils/saleLoanIdentifier';
import {
  ApprovalStatusColumnHeader,
  SettlementStatusColumnHeader,
} from '../components/Sales/SaleStatusColumnHeaders';

const MySwal = withReactContent(Swal);

const getCssToken = (tokenName) => {
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();
  return value;
};

const DocumentsPage = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [mobileToken, setMobileToken] = useState(null);
  const [branchToken, setBranchToken] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  // DocumentsPage excludes invoice_required; those sales are handled from ObservationPage.
  const [filters, setFilters] = useState({
    search: '',
    approval_status: 'approved',
    settlement_status: 'pending_docs'
  });
  const [approvalStatusOptions, setApprovalStatusOptions] = useState([]);
  const [settlementStatusOptions, setSettlementStatusOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [uploadingDocuments, setUploadingDocuments] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchSales();
  }, [filters]);

  // Solo enviar búsqueda a la API con 0 o >= 3 caracteres (backend exige min:3)
  useEffect(() => {
    const initialSearch = searchInput.length === 0 && !filters.search;
    if (!initialSearch && (searchInput.length === 0 || searchInput.length >= 3)) {
      setFilters(prev => ({
        ...prev,
        search: searchInput
      }));
    }
  }, [searchInput]);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const searchValue = (filters.search.length === 0 || filters.search.length >= 3)
        ? filters.search
        : '';
      const response = await api.get('/sales', { 
        params: { 
          search: searchValue,
          approval_status: filters.approval_status || '',
          settlement_status: filters.settlement_status || ''
        } 
      });

      setSales(response.data.sales);
      if (response.data.filters?.approval_status_options?.length) {
        setApprovalStatusOptions(response.data.filters.approval_status_options);
      }
      if (response.data.filters?.settlement_status_options?.length) {
        setSettlementStatusOptions(response.data.filters.settlement_status_options);
      }
    } catch (error) {
      console.error('Error al obtener ventas:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las ventas',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setSearchInput(value);
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const showQRModal = (url, title, description, whatsappUrl = null) => {
    MySwal.fire({
      title,
      html: `
        <div class="text-center">
          <canvas id="qr-canvas" class="mb-4"></canvas>
          <p class="mb-3">${description}</p>
          ${whatsappUrl ? `
            <hr />
            <div class="row g-3">
              <div class="col-6">
                <p class="text-muted small mb-2">Copia este enlace:</p>
                <div class="input-group">
                  <input 
                    type="text" 
                    value="${url}" 
                    class="form-control form-control-sm"
                    readonly
                  />
                  <button 
                    class="btn btn-outline-secondary copy-button"
                    type="button"
                    onclick="copyToClipboard('${url}', this)"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
              <div class="col-6">
                <p class="text-muted small mb-2">O comparte con el cliente:</p>
                <a 
                  href="${whatsappUrl}" 
                  target="_blank" 
                  class="btn btn-success w-100"
                >
                  <i class="bi bi-whatsapp me-2"></i>
                  Enviar por WhatsApp
                </a>
              </div>
            </div>
          ` : `
            <p class="text-muted small mb-2">O copia este enlace:</p>
            <div class="input-group">
              <input 
                type="text" 
                value="${url}" 
                class="form-control form-control-sm"
                readonly
              />
              <button 
                class="btn btn-outline-secondary btn-sm copy-button"
                type="button"
                onclick="copyToClipboard('${url}', this)"
              >
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
          `}
          <small class="text-muted d-block mt-3">
            El acceso expirará al final del día
          </small>
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      customClass: {
        popup: 'rg-modal'
      },
      didRender: () => {
        const canvas = document.getElementById('qr-canvas');
        QRCode.toCanvas(canvas, url, {
          width: 256,
          margin: 1,
          color: {
            dark: getCssToken('--rg-color-text-primary'),
            light: getCssToken('--rg-color-background-primary')
          }
        });

        // Add copy function to window scope
        window.copyToClipboard = function(text, button) {
          navigator.clipboard.writeText(text);
          button.innerHTML = '<i class="bi bi-check-lg"></i>';
          // button.classList.add('btn-success');
          setTimeout(() => {
            button.innerHTML = '<i class="bi bi-clipboard"></i>';
            // button.classList.remove('btn-success');
          }, 2000);
        };
      }
    });
  };

  const handleShowBranchQR = async () => {
    try {
      if (mobileToken) {
        const mobileUrl = `${window.location.origin}/m/docs/${mobileToken}`;
        showQRModal(
          mobileUrl,
          'Acceso móvil a documentación',
          'Escanea el código QR con la cámara de tu celular para ver todas las ventas pendientes'
        );
        return;
      }

      setGeneratingToken(true);
      const response = await TokenService.generateBranchToken(); // Usa 120 min por defecto
      
      const token = response.data.token;
      setMobileToken(token);
      
      const mobileUrl = TokenService.generateMobileUrl(token, 'branch');
      showQRModal(
        mobileUrl,
        'Acceso móvil a documentación',
        'Escanea el código QR con la cámara de tu celular para ver todas las ventas pendientes'
      );
    } catch (error) {
      console.error('Error al generar token de sucursal:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudo generar el acceso móvil',
        icon: 'error'
      });
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleGenerateMobileToken = async (saleId, customerId) => {
    try {
      const response = await TokenService.generateSaleToken(
        saleId,
        customerId,
        30
      );

      const { token } = response.data;
      const mobileUrl = TokenService.generateMobileUrl(token, 'sale');
      
      showQRModal(
        mobileUrl,
        'Acceso móvil para esta venta',
        'Escanea el código QR con la cámara de tu celular para subir los documentos de esta venta'
      );
    } catch (error) {
      console.error('Error al generar token móvil:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudo generar el acceso móvil',
        icon: 'error'
      });
    }
  };

  const handleShareWithCustomer = async (sale) => {
    try {
      const response = await TokenService.generateCustomerToken(
        sale.id,
        sale.customer.id,
        15
      );

      const { token } = response.data;
      const customerUrl = TokenService.generateMobileUrl(token, 'customer');
      const whatsappText = encodeURIComponent(
        `Hola ${sale.customer.name}, aquí puedes subir los documentos pendientes de tu compra: ${customerUrl}`
      );
      const phoneDigits = sale.customer.phone ? String(sale.customer.phone).replace(/\D/g, '') : '';
      const whatsappUrl = phoneDigits ? `https://wa.me/${phoneDigits}?text=${whatsappText}` : `https://wa.me/?text=${whatsappText}`;

      showQRModal(
        customerUrl,
        'Solicitar al cliente',
        'El cliente puede escanear este código QR para subir sus documentos',
        whatsappUrl
      );
    } catch (error) {
      MySwal.fire({
        title: 'Error',
        text: 'No se pudo generar el enlace para el cliente',
        icon: 'error'
      });
    }
  };

  const handleFileUpload = (saleId, docType, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadingDocuments(prev => ({
        ...prev,
        [docType]: {
          file,
          preview: reader.result,
          status: 'uploaded'
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleReject = (docType) => {
    setUploadingDocuments(prev => ({
      ...prev,
      [docType]: null
    }));
  };

  const handleUploadDocuments = async (saleId) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      const files = [];
      const documentIds = [];
      const documentSources = [];
      const categories = [];

      Object.entries(uploadingDocuments).forEach(([docType, docData]) => {
        if (docData?.file) {
          const pendingDoc = selectedSale.pending_documents.find(doc => doc.type === docType);
          if (pendingDoc) {
            files.push(docData.file);
            documentIds.push(pendingDoc.id);
            // document_sources debe ser 'loan' o 'purchase_request'
            documentSources.push(pendingDoc.source || 'loan');
            // categories debe ser el type del documento
            categories.push(pendingDoc.type);
          }
        }
      });

      files.forEach((file, index) => {
        formData.append('files[]', file);
      });
      
      documentIds.forEach(id => {
        formData.append('document_ids[]', id);
      });

      documentSources.forEach(source => {
        formData.append('document_sources[]', source);
      });

      categories.forEach(category => {
        formData.append('categories[]', category);
      });

      const response = await api.post(
        `/customers/${selectedSale.customer.id}/sales/${saleId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (event) => {
            const progressValue = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progressValue);
          },
        }
      );

      // Actualizamos el estado de la venta en el listado (scoped statuses from upload response)
      setSales(prevSales => 
        prevSales.map(sale => {
          if (sale.id === saleId) {
            return {
              ...sale,
              status: response.data.status ?? sale.status,
              approval_status: response.data.approval_status ?? sale.approval_status,
              settlement_status: response.data.settlement_status !== undefined
                ? response.data.settlement_status
                : sale.settlement_status,
              pending_documents: sale.pending_documents.filter(
                doc => !documentIds.includes(doc.id)
              )
            };
          }
          return sale;
        })
      );

      MySwal.fire({
        icon: 'success',
        title: 'Documentos subidos',
        text: 'Los documentos se han subido correctamente'
      });

      setShowUploadModal(false);
      setUploadingDocuments({});

    } catch (error) {
      console.error('Error al subir documentos:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al subir los documentos'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderPendingDocuments = (documents) => {
    if (!documents || documents.length === 0) {
      return (
        <span className="text-muted">
          <i className="bi bi-check-circle text-success me-1"></i>
          Sin documentos pendientes
        </span>
      );
    }

    return (
      <ul className="list-unstyled mb-0">
        {documents.map((doc, index) => (
          <li key={index} className="mb-1">
            <i className="bi bi-exclamation-circle text-warning me-1"></i>
            {doc.title}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3>Digitalización de Documentos</h3>
            <p className="text-subtitle text-muted">Sube los documentos requeridos para completar las operaciones</p>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <button 
              className="btn btn-outline-primary float-end"
              onClick={handleShowBranchQR}
              disabled={generatingToken}
            >
              {generatingToken ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Generando acceso...
                </>
              ) : (
                <>
                  <i className="bi bi-qr-code me-2"></i>
                  Cargar desde Móvil
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="card">
          <div className="card-header">
            <div className="row g-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por cliente o venta..."
                  name="search"
                  value={searchInput}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  name="approval_status"
                  value={filters.approval_status}
                  onChange={handleFilterChange}
                >
                  <option value="">Aprobación (todas)</option>
                  {approvalStatusOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  name="settlement_status"
                  value={filters.settlement_status}
                  onChange={handleFilterChange}
                >
                  <option value="">Liquidación (todas)</option>
                  {settlementStatusOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>N° Prestamo</th>
                    <th>Cliente</th>
                    <th>Fecha</th>
                    <th>Monto Total</th>
                    <ApprovalStatusColumnHeader className="" />
                    <SettlementStatusColumnHeader className="" />
                    <th>Documentos Pendientes</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id}>
                      <td>{getSaleLoanIdentifierDisplay(sale)}</td>
                      <td>
                        <button 
                          type="button"
                          onClick={(e) => {
                            navigate(`/clientes/${sale.customer.dni}`);
                          }}
                          className="rg-link-button"
                        >
                          {sale.customer.name || `Cliente ${sale.customer.dni}`}
                        </button>
                        <br />
                        <small className="text-muted">
                          DNI: {sale.customer.dni}
                        </small>
                      </td>
                      <td>{new Date(sale.sale_date).toLocaleDateString()}</td>
                      <td>${formatNumber(sale.amount)}</td>
                      <td>
                        <span className={`sale-status-badge ${getApprovalStatusClass(sale)}`}>
                          {getApprovalStatusLabel(sale)}
                        </span>
                      </td>
                      <td>
                        {getSettlementStatusLabel(sale) === '—' ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <span className={`sale-status-badge ${getSettlementStatusClass(sale)}`}>
                            {getSettlementStatusLabel(sale)}
                          </span>
                        )}
                      </td>
                      <td>
                        {renderPendingDocuments(sale.pending_documents)}
                      </td>
                      <td>
                        <div className="btn-group">
                          <button 
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              setSelectedSale(sale);
                              setShowUploadModal(true);
                            }}
                            disabled={!sale.pending_documents?.length}
                            title={sale.pending_documents?.length ? "Subir desde computadora" : "No hay documentos pendientes"}
                          >
                            <i className="bi bi-upload me-1"></i>
                            Subir
                          </button>
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleGenerateMobileToken(sale.id, sale.customer.id)}
                            disabled={!sale.pending_documents?.length}
                            title={sale.pending_documents?.length ? "Generar acceso móvil" : "No hay documentos pendientes"}
                          >
                            <i className="bi bi-phone me-1"></i>
                            Escanear
                          </button>
                          <button
                            className="btn btn-outline-info btn-sm"
                            onClick={() => handleShareWithCustomer(sale)}
                            disabled={!sale.pending_documents?.length}
                            title={sale.pending_documents?.length ? "Solicitar al cliente" : "No hay documentos pendientes"}
                          >
                            <i className="bi bi-share me-1"></i>
                            Solicitar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de carga de documentos */}
      {showUploadModal && selectedSale && (
        <>  
        <div className="modal-backdrop fade show"></div>
        <div className="modal fade show document-upload-modal home-modal" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Cargar Documentos — N° Prestamo {getSaleLoanIdentifierDisplay(selectedSale)}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadingDocuments({});
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="document-upload-container">
                  <p className="text-center text-muted mb-4">
                    Arrastra y suelta los documentos en las áreas correspondientes o haz clic para seleccionar los archivos.
                  </p>
                  <ul className="document-list">
                    {selectedSale.pending_documents?.map((doc) => (
                      <DocumentItem
                        key={doc.type}
                        icon={
                          doc.type === 'dni_front' ? faIdCard :
                          doc.type === 'dni_back' ? faIdCardAlt :
                          doc.type === 'service' ? faFileInvoiceDollar :
                          doc.type === 'contract' ? faFileAlt :
                          faFile
                        }
                        name={doc.title}
                        status={uploadingDocuments[doc.type]?.status || 'pending'}
                        onFileUpload={(file) => handleFileUpload(selectedSale.id, doc.type, file)}
                        filePreview={uploadingDocuments[doc.type]?.preview}
                        onReject={() => handleReject(doc.type)}
                        isUploading={isUploading}
                      />
                    ))}
                  </ul>

                  {isUploading && (
                    <div className="mt-4">
                      <div className="progress">
                        <div 
                          className="progress-bar progress-bar-striped progress-bar-animated" 
                          role="progressbar" 
                          style={{ width: `${uploadProgress}%` }} 
                          aria-valuenow={uploadProgress} 
                          aria-valuemin="0" 
                          aria-valuemax="100"
                        >
                          {uploadProgress}%
                        </div>
                      </div>
                      <p className="text-center mt-2">Subiendo documentos...</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadingDocuments({});
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleUploadDocuments(selectedSale.id)}
                  disabled={
                    isUploading || 
                    !Object.keys(uploadingDocuments).length || 
                    !Object.values(uploadingDocuments).every(doc => doc?.status === 'uploaded')
                  }
                >
                  {isUploading ? 'Subiendo...' : 'Subir Documentos'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default DocumentsPage; 